// Tiny in-SPA meta/OG manager. The marketing Worker now injects route-specific
// tags into the first HTML response for crawlers and social scrapers; this
// hook keeps the same tags in sync after client-side navigation inside the SPA.
import { useEffect } from "react";
import { formatDocumentTitle, DEFAULT_OG_IMAGE, SITE_NAME } from "../route-meta";

export type DocumentMeta = {
  title: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
};

function setMeta(selector: string, attrs: Record<string, string>) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
    return el;
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

export function useDocumentMeta(meta: DocumentMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = formatDocumentTitle(meta.title);

    const desc = meta.description ?? "";
    const ogTitle = meta.ogTitle ?? meta.title;
    const ogDesc = meta.ogDescription ?? desc;
    const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;
    const canonical = meta.canonical ?? `https://px7.training${window.location.pathname}`;

    setMeta('meta[name="description"]', { name: "description", content: desc });
    setMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: ogDesc });
    setMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: ogTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: ogDesc });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    setLink("canonical", canonical);

    return () => {
      document.title = prevTitle;
    };
  }, [meta.title, meta.description, meta.ogTitle, meta.ogDescription, meta.ogImage, meta.canonical]);
}
